import type { ChoiceLetter, ConditionalInstruction, MatchTactic } from '@/game/types'

interface TacticalOverridesProps {
  round: number
  opponentName: string
  opponentStyle: string
  opponentStrength: string
  opponentWeakness: string
  tactics: MatchTactic[]
  conditionalInstruction: ConditionalInstruction | null
  researchInsights: string[]
  onTacticChange: (id: MatchTactic['id'], choice: ChoiceLetter) => void
  onConditionalChange: (instruction: ConditionalInstruction) => void
  onRestore: (staffConditional: ConditionalInstruction) => void
  onBack: () => void
  onConfirm: () => void
}

const IMPACTS: Record<string, { benefit: string; tradeoff: string }> = {
  'D6.1-A': { benefit: 'Stable transitions with moderate risk.', tradeoff: 'No specialist territory or possession edge.' },
  'D6.1-B': { benefit: 'Faster territory gain and earlier entries.', tradeoff: 'More turnover exposure behind the ball.' },
  'D6.1-C': { benefit: 'Controls possession and transition risk.', tradeoff: 'Slower entries give the defence time to reset.' },
  'D6.2-A': { benefit: 'Stretches the defence and protects outside outlets.', tradeoff: 'Longer kicks can isolate forwards.' },
  'D6.2-B': { benefit: 'Keeps both corridor and boundary available.', tradeoff: 'Creates no strong spatial mismatch.' },
  'D6.2-C': { benefit: 'Creates fast, high-value corridor attacks.', tradeoff: 'Turnovers expose the centre of the ground.' },
  'D6.3-A': { benefit: 'Protects shape around the contest.', tradeoff: 'May concede first possession.' },
  'D6.3-B': { benefit: 'Balances numbers at the ball with outside coverage.', tradeoff: 'Neither advantage is maximised.' },
  'D6.3-C': { benefit: 'Creates pressure, repeat entries and turnovers.', tradeoff: 'Leaves less protection outside the contest.' },
  'D6.4-A': { benefit: 'Balances territory pressure and protection.', tradeoff: 'Reactive against extreme opposition shapes.' },
  'D6.4-B': { benefit: 'Protects dangerous space behind the defence.', tradeoff: 'Concedes territory and easier short possession.' },
  'D6.4-C': { benefit: 'Compresses the ground and supports pressure.', tradeoff: 'Vulnerable when the press is beaten.' },
  'D6.5-A': { benefit: 'Adapts tempo to match phase and player condition.', tradeoff: 'Does not force a specific game shape.' },
  'D6.5-B': { benefit: 'Reduces opposition run and protects fatigued players.', tradeoff: 'Lowers Kingsport’s scoring volume.' },
  'D6.5-C': { benefit: 'Maximises scoring chances and running pressure.', tradeoff: 'Raises fatigue and late defensive risk.' },
}

const conditionalOptions = (opponentName: string): ConditionalInstruction[] => [
  { id: 'D7-A', label: 'Close the corridor after consecutive rebound scores', description: `Staff recommendation against ${opponentName}'s transition game.`, condition: `${opponentName} scores twice from uninterrupted defensive rebound chains.`, action: 'Add corridor pressure while the wings protect the release kick.', isAvailable: true, isSelected: true },
  { id: 'D7-B', label: 'Introduce Ari earlier if transition speed drops', description: 'Use youth running power before the final quarter.', condition: 'Kingsport records two consecutive slow or intercepted exits.', action: 'Move Ari into the primary midfield rotation.', isAvailable: true, isSelected: false },
  { id: 'D7-C', label: 'Increase pressure if trailing', description: 'Trade structure for turnover opportunities.', condition: 'Kingsport trails by 18 or more points after half-time.', action: 'Send an extra midfielder to the contest and lift forward pressure.', isAvailable: true, isSelected: false },
  { id: 'D7-D', label: 'No conditional instruction', description: 'Leave every match-day adjustment to the live staff call.', condition: 'None.', action: 'Maintain the starting plan.', isAvailable: true, isSelected: false },
]

export function TacticalOverrides({
  round,
  opponentName,
  opponentStyle,
  opponentStrength,
  opponentWeakness,
  tactics,
  conditionalInstruction,
  researchInsights,
  onTacticChange,
  onConditionalChange,
  onRestore,
  onBack,
  onConfirm,
}: TacticalOverridesProps) {
  const instructions = conditionalOptions(opponentName)
  const staffConditional = instructions[0]
  const tacticOverrideCount = tactics.filter(tactic => tactic.currentChoice !== tactic.staffChoice).length
  const conditionalOverridden = conditionalInstruction?.id !== staffConditional.id
  const overrideCount = tacticOverrideCount + (conditionalOverridden ? 1 : 0)

  return (
    <div className="screen tactics-screen">
      <button className="back-link" type="button" onClick={onBack}>Back to dashboard</button>
      <div className="page-title tactics-title">
        <div>
          <span className="eyebrow">Match committee - Round {round}</span>
          <h1>Tactical Overrides</h1>
          <p>Staff plan for {opponentName}'s {opponentStyle.toLowerCase()} game. Change one dimension or rebuild the whole match shape.</p>
        </div>
        <div className="tactics-title-actions">
          <span className={`status-pill ${overrideCount > 0 ? 'override' : ''}`}>{overrideCount > 0 ? `${overrideCount} Coach override${overrideCount === 1 ? '' : 's'}` : 'All staff choices'}</span>
          <button className="text-button" type="button" disabled={overrideCount === 0} onClick={() => onRestore(staffConditional)}>{overrideCount === 0 ? 'Staff Tactics Active' : 'Restore Staff Tactics'}</button>
        </div>
      </div>

      <section className="tactical-matchup panel">
        <div><span>Opponent strength</span><strong>{opponentStrength}</strong></div>
        <div><span>Target weakness</span><strong>{opponentWeakness}</strong></div>
        <div><span>Operating rule</span><strong>Improve the odds; do not expect certainty.</strong></div>
      </section>

      <section className={`panel tactical-intelligence ${researchInsights.length ? 'researched' : ''}`}>
        <div><span className="eyebrow">Research briefing</span><strong>{researchInsights.length ? `${researchInsights.length} actionable finding${researchInsights.length === 1 ? '' : 's'}` : 'Staff baseline only'}</strong></div>
        {researchInsights.length ? <ul>{researchInsights.map(insight => <li key={insight}>{insight}</li>)}</ul> : <p>No investigation selected. The staff plan remains competent, but opponent triggers and exact mismatches are uncertain.</p>}
      </section>

      <section className="tactic-editor" aria-label="Tactical dimensions">
        {tactics.map(tactic => (
          <fieldset className={`tactic-editor-row ${tactic.currentChoice !== tactic.staffChoice ? 'coach-override' : ''}`} key={tactic.id}>
            <legend><span>{tactic.id}</span>{tactic.dimension}<em>{tactic.currentChoice === tactic.staffChoice ? 'Staff choice' : 'Coach override'}</em></legend>
            <div className="tactic-options">
              {tactic.options.map(option => {
                const impact = IMPACTS[`${tactic.id}-${option.letter}`]
                const selected = option.letter === tactic.currentChoice
                return (
                  <label className={`tactic-option ${selected ? 'selected' : ''} ${option.letter === tactic.staffChoice ? 'staff-option' : ''}`} key={option.letter}>
                    <input type="radio" name={tactic.id} value={option.letter} checked={selected} onChange={() => onTacticChange(tactic.id, option.letter)} />
                    <span className="tactic-option-code">{option.letter}</span>
                    <span className="tactic-option-copy">
                      <strong>{option.label}{option.letter === tactic.staffChoice && <small>Staff</small>}</strong>
                      <span>{option.description}</span>
                      <b>Benefit: {impact?.benefit}</b>
                      <i>Trade-off: {impact?.tradeoff}</i>
                    </span>
                  </label>
                )
              })}
            </div>
          </fieldset>
        ))}
      </section>

      <fieldset className={`conditional-editor panel ${conditionalOverridden ? 'coach-override' : ''}`}>
        <legend>D7 - Conditional instruction <em>{conditionalOverridden ? 'Coach override' : 'Staff choice'}</em></legend>
        <div className="conditional-options">
          {instructions.map(instruction => (
            <label className={`conditional-option ${conditionalInstruction?.id === instruction.id ? 'selected' : ''}`} key={instruction.id}>
              <input type="radio" name="conditional-instruction" checked={conditionalInstruction?.id === instruction.id} onChange={() => onConditionalChange(instruction)} />
              <span><strong>{instruction.id} - {instruction.label}{instruction.id === 'D7-A' && <small>Staff</small>}</strong><b>If:</b> {instruction.condition}<b>Then:</b> {instruction.action}<i>{instruction.description}</i></span>
            </label>
          ))}
        </div>
      </fieldset>

      <section className="panel tradeoff"><h2>Expected match shape</h2><div><p><strong>Preparation</strong> The staff plan targets {opponentName}'s primary strength: {opponentStrength.toLowerCase()}.</p><p><strong>Mismatch</strong> Kingsport will attack {opponentWeakness.toLowerCase()} when the opportunity appears.</p><p><strong>Uncertainty</strong> Overrides alter the deterministic prototype result and its explanation; preparation never removes variance.</p></div></section>

      <div className="sticky-actions"><span>{overrideCount > 0 ? `${overrideCount} Coach override${overrideCount === 1 ? '' : 's'} saved` : 'Staff tactical plan retained'}</span><button className="primary-action" type="button" onClick={onConfirm}>Confirm match plan — Return to Week</button></div>
    </div>
  )
}
